import React, { useState } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  Button,
  Chip,
  Skeleton,
  Tooltip,
  Checkbox
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

import { ALL_PAGES, getRoleColor } from "../../auth/roles";

function RoleTable({
  roles,
  loading,
  onEdit,
  onDelete,
  selectedIds = [],
  onSelectionChange
}) {
  const [page, setPage] = useState(1);

  const rowsPerPage = 50;

  const sortedRoles = [...roles].sort(
    (a, b) => a.role_id - b.role_id
  );

  const startIndex = (page - 1) * rowsPerPage;

  const paginatedRoles = sortedRoles.slice(
    startIndex,
    startIndex + rowsPerPage
  );

  const totalPages = Math.ceil(
    roles.length / rowsPerPage
  );

  const pageLabel = (pageKey) => ALL_PAGES.find((p) => p.key === pageKey)?.label || pageKey;

  // Built-in roles can't be deleted, so they're excluded from selection entirely.
  const deletableRoles = roles.filter((role) => !role.is_system);

  const allSelected = deletableRoles.length > 0 && selectedIds.length === deletableRoles.length;

  const isSelected = (roleId) => selectedIds.includes(roleId);

  const toggleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange?.(deletableRoles.map((role) => role.role_id));
      return;
    }

    onSelectionChange?.([]);
  };

  const toggleSelectOne = (roleId, checked) => {
    if (checked) {
      onSelectionChange?.([...selectedIds, roleId]);
      return;
    }

    onSelectionChange?.(selectedIds.filter((id) => id !== roleId));
  };

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)"
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "background.default" }}>
              <TableCell
                sx={{
                  width: 56,
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#475569"
                }}
              >
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedIds.length > 0 && !allSelected}
                  onChange={(event) => toggleSelectAll(event.target.checked)}
                />
              </TableCell>

              {[
                "Role Name",
                "Pages",
                "Actions"
              ].map((head) => (
                <TableCell
                  key={head}
                  sx={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#475569"
                  }}
                >
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && Array.from({ length: 5 }).map((_, index) => (
              <TableRow key={`role-skeleton-${index}`}>
                <TableCell>
                  <Skeleton variant="circular" width={24} height={24} />
                </TableCell>

                <TableCell>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Skeleton variant="circular" width={24} height={24} />
                    <Skeleton variant="text" width={140} height={24} />
                  </Box>
                </TableCell>

                <TableCell>
                  <Skeleton variant="text" width="70%" height={24} />
                </TableCell>

                <TableCell>
                  <Box display="flex" gap={1}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="circular" width={32} height={32} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}

            {!loading && paginatedRoles.map((role) => (
              <TableRow
                key={role.role_id}
                hover
                sx={{
                  "& td": {
                    borderBottom: "1px solid",
                    borderColor: "divider"
                  }
                }}
              >
                <TableCell>
                  {role.is_system ? (
                    <Tooltip title="Built-in roles can't be selected for deletion">
                      <span>
                        <Checkbox checked={false} disabled />
                      </span>
                    </Tooltip>
                  ) : (
                    <Checkbox
                      checked={isSelected(role.role_id)}
                      onChange={(event) => toggleSelectOne(role.role_id, event.target.checked)}
                    />
                  )}
                </TableCell>

                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        flexShrink: 0,
                        background: getRoleColor(role.role_name).color
                      }}
                    />

                    {role.is_system && (
                      <Tooltip title="Built-in role">
                        <AdminPanelSettingsOutlinedIcon fontSize="small" sx={{ color: "#2563eb" }} />
                      </Tooltip>
                    )}

                    <Typography fontSize={14} fontWeight={600} sx={{ textTransform: "capitalize" }}>
                      {role.role_name.replace(/_/g, " ")}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 500 }}>
                    {role.pages.length === 0 && (
                      <Typography fontSize={12} color="textSecondary">No pages assigned</Typography>
                    )}

                    {role.pages.map((page) => (
                      <Tooltip
                        key={page.page_key}
                        title={`${page.data_scope === "own" ? "Own data" : "All data"} \u2022 ${page.access_level === "read" ? "Read only" : "Write access"}`}
                      >
                        <Chip
                          label={pageLabel(page.page_key)}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: 12,
                            background: page.access_level === "read" ? "#f1f5f9" : "#eff6ff",
                            color: page.access_level === "read" ? "#475569" : "#2563eb"
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </TableCell>

                <TableCell>
                  <Box display="flex" gap={1}>
                    <IconButton
                      onClick={() => onEdit(role)}
                      sx={{
                        background: "#eff6ff",
                        width: 32,
                        height: 32
                      }}
                    >
                      <EditIcon
                        fontSize="small"
                        sx={{
                          color: "#2563eb"
                        }}
                      />
                    </IconButton>

                    {role.is_system ? (
                      <Tooltip title="Built-in roles can't be deleted">
                        <span>
                          <IconButton disabled sx={{ width: 32, height: 32 }}>
                            <LockOutlinedIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <IconButton
                        onClick={() => onDelete(role)}
                        sx={{
                          background: "#fef2f2",
                          width: 32,
                          height: 32
                        }}
                      >
                        <DeleteIcon
                          fontSize="small"
                          sx={{
                            color: "#ef4444"
                          }}
                        />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}

            {!loading && paginatedRoles.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} sx={{ py: 6 }}>
                  <Box
                    sx={{
                      maxWidth: 520,
                      mx: "auto",
                      textAlign: "center",
                      borderRadius: 3,
                      border: "1px solid #dbeafe",
                      background: "linear-gradient(145deg, #f8fbff 0%, #eef6ff 100%)",
                      p: 4
                    }}
                  >
                    <Box
                      sx={{
                        width: 58,
                        height: 58,
                        mx: "auto",
                        mb: 1.8,
                        borderRadius: "50%",
                        background: "#dbeafe",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#1d4ed8"
                      }}
                    >
                      <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 30 }} />
                    </Box>

                    <Typography variant="h6" sx={{ color: "#1e3a8a", fontWeight: 700, mb: 0.7 }}>
                      No roles found
                    </Typography>

                    <Typography sx={{ color: "#475569", fontSize: 14, mb: 1.8 }}>
                      Try changing search text or add a new role to control page access.
                    </Typography>

                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.7,
                        px: 1.4,
                        py: 0.6,
                        borderRadius: 999,
                        background: "#dbeafe",
                        color: "#1d4ed8",
                        fontSize: 12,
                        fontWeight: 700
                      }}
                    >
                      <AutoAwesomeRoundedIcon sx={{ fontSize: 14 }} />
                      Role list is empty
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && roles.length > 0 && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={2}
        >
          <Typography fontSize={13} color="#64748b">
            Showing {roles.length === 0 ? 0 : startIndex + 1}
            {" "}
            to
            {" "}
            {Math.min(startIndex + rowsPerPage, roles.length)}
            {" "}
            of {roles.length} results
          </Typography>

          <Box display="flex" alignItems="center" gap={1}>
            <Button
              size="small"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeftIcon />
            </Button>

            <Button
              variant="contained"
              size="small"
              sx={{
                minWidth: 32
              }}
            >
              {page}
            </Button>

            <Button
              size="small"
              disabled={page === totalPages || totalPages === 0}
              onClick={() => setPage(page + 1)}
            >
              <ChevronRightIcon />
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default RoleTable;
